/**
 * AIP 插件详情页文案（营销层唯一来源）。
 *
 * 这里只放静态内容；名称/版本/图标/版本列表来自接口。
 * 不包含价格与订阅逻辑——授权和续费在插件端完成。
 */
export const AIP_PLUGIN_CONTENT = {
  ereplace: {
    key: 'ereplace',
    en: 'EREPLACE',
    name: '批量对象替换',
    tagline: '选中一个模板，所有同形状对象一次换完。',
    summary: '按贝塞尔节点逐个比对，只替换真正同形状的对象；替换后回到原中心，保持层级。适合刀模、图标、logo 在稿子里重复出现的场景。',
    accent: '#2F5BFF',
    cover: 'ereplace',
    heroGif: '/eprinttools-files/front-end/features/ereplace/ereplace-find-mark.gif',
    chips: ['几何匹配', '批量替换', '原位保持'],
    ai: 'Illustrator 2019+',
    platform: 'Windows 10/11',
    format: 'ZIP',
    trialDays: 30,
    versionNote: '适用于 Illustrator 2019 以上版本。替换不缩放，只平移到原对象中心。',
    pains: [
      { title: '同一个图形出现几十次', body: '逐个选中、替换、再对齐，时间都花在重复动作上，还容易漏掉角落里的一个。' },
      { title: '“选择相同”多选或漏选', body: '近似尺寸、不同子路径的复合路径，靠肉眼和自带选择很难判断。' },
      { title: '替换后位置和层级乱了', body: '新对象没落在原中心，或者跑到别的图层上面，返工比原操作更久。' }
    ],
    steps: [
      { title: '选模板', body: '选中一个复合路径或普通路径。' },
      { title: '查找', body: '按几何完全一致匹配，标记色高亮，面板显示数量。' },
      { title: '选替换对象', body: '选中新的路径或复合路径。' },
      { title: '一键替换', body: '逐个替换到原中心，保持层级；不缩放。' }
    ],
    features: [
      { title: '节点级几何匹配', body: '先比较子路径数、总段数和真实极值边界，再把贝塞尔控制点归一化后逐点比对；宽高误差控制在约 0.03pt 内，形状差一点都不会误命中。', large: true, gif: '/eprinttools-files/front-end/features/ereplace/ereplace-node-match.gif' },
      { title: '批量查找与标记', body: '选中模板后扫描当前文档，把所有命中对象临时填成标记色并显示数量；标记色可自选，锁定和隐藏对象会跳过。', large: true, gif: '/eprinttools-files/front-end/features/ereplace/ereplace-find-mark.gif' },
      { title: '原位替换', body: '复制替换对象后按旧对象中心平移，沿用前一个兄弟节点保持堆叠顺序；不缩放，新对象保持自己的尺寸。', gif: '/eprinttools-files/front-end/features/ereplace/ereplace-replace.gif' },
      { title: '安全替换边界', body: '替换目标不能同时出现在查找结果里，避免自我覆盖；模板和目标都支持路径与复合路径。', gif: '/eprinttools-files/front-end/features/ereplace/ereplace-safe-boundary.gif' }
    ],
    audiences: [
      { title: '包装印前', body: '刀模、图标、logo 在多个位置重复出现。', icon: '包' },
      { title: '标签制版', body: '多 SKU 共用图形，改版时批量换标。', icon: '标' },
      { title: '品牌完稿', body: '客户改了一个图形，需要全稿同步。', icon: '品' }
    ],
    boundaries: [
      '只匹配几何完全一致的对象；不按文字内容、图层名、颜色匹配。',
      '模板和替换对象必须是路径/复合路径；文字请先转曲。',
      '替换不缩放；需要按尺寸套版请用“对象替换”。',
      '“查找”会修改填充色做标记，建议先保存副本。'
    ],
    faqs: [
      { q: '和 Illustrator 的“选择相同”有什么区别？', a: '选择相同主要看外观/类型；本插件会比对子路径数、段数和贝塞尔控制点，形状差一点就不会命中。' },
      { q: '替换会改变大小吗？', a: '不会。只平移到原对象中心，保持新对象自己的尺寸。需要按尺寸缩放，请用“对象替换”。' },
      { q: '为什么查找后图形变红了？', a: '红色是标记色，用来让你看到哪些对象被命中；可以用面板颜色按钮改颜色。' },
      { q: '支持文字对象吗？', a: '模板和替换对象需要是路径或复合路径；文字请先转曲。' },
      { q: '替换错了能撤销吗？', a: '用 Illustrator 的撤销（Ctrl+Z）。批量操作前建议先保存副本。' }
    ]
  },

  objreplacer: {
    key: 'objreplacer',
    en: 'OBJREPLACER',
    name: '对象替换',
    tagline: '把目标对象，按源对象的尺寸和锚点套进去。',
    summary: '九宫格锚点、水平/垂直/等比缩放、保留原对象。换刀模、换版型时，不再手动缩放和对齐。',
    accent: '#7C3AED',
    cover: 'objreplacer',
    heroGif: '/eprinttools-files/front-end/features/objreplacer/objreplacer-scale.gif',
    chips: ['九宫格锚点', '等比缩放', '原位替换'],
    ai: 'Illustrator 2019+',
    platform: 'Windows 10/11',
    format: 'ZIP',
    trialDays: null,
    trialLabel: '永久免费',
    versionNote: '当前版本按 X 坐标排序取源/目标：左侧为源，右侧为目标。',
    pains: [
      { title: '换刀模要重新套版', body: '新刀模和旧刀模比例不同，图形要重新缩放、对齐，反复几次才能对上。' },
      { title: '锚点选错，整张图偏移', body: '左上、中心、右下，不同版式要对不同锚点；每次都靠肉眼，容易差几毫米。' },
      { title: '复制、缩放、移动、调层级', body: '步骤一多就乱，保留原对象时更容易放错位置。' }
    ],
    steps: [
      { title: '选中两个对象', body: '源对象在左，目标对象在右。' },
      { title: '设置缩放', body: '水平 / 垂直 / 等比，按需勾选。' },
      { title: '设置锚点', body: '九宫格、保留原对象、置于下方。' },
      { title: '点替换', body: '目标对象被缩放、对齐、落到源对象位置。' }
    ],
    features: [
      { title: '九宫格锚点', body: '源对象和目标对象各取 3×3 九个参考点；替换时把目标对象的指定锚点对齐到源对象的同一锚点，不靠肉眼估位置。', large: true, gif: '/eprinttools-files/front-end/features/objreplacer/objreplacer-anchor.gif' },
      { title: '精确边界', body: '用贝塞尔曲线极值计算真实边界，不依赖普通包围盒；对旋转、复合路径和异形轮廓更可靠。', large: true, gif: '/eprinttools-files/front-end/features/objreplacer/objreplacer-precise-bounds.gif' },
      { title: '水平/垂直/等比', body: '水平、垂直、等比三种缩放基准；等比取宽高比例中较小的一个，保证不拉伸，可能留白。', gif: '/eprinttools-files/front-end/features/objreplacer/objreplacer-scale.gif' },
      { title: '保留原对象', body: '勾选后不删除源对象，只把目标对象缩放并套上去，适合新旧版对比或保留旧稿。', gif: '/eprinttools-files/front-end/features/objreplacer/objreplacer-keep-source.gif' },
      { title: '置于下方', body: '保留源对象时，可把替换后的对象放到源对象下方，避免遮挡；未勾选保留时不可用。', gif: '/eprinttools-files/front-end/features/objreplacer/objreplacer-place-below.gif' },
      { title: '左侧为源，右侧为目标', body: '插件按 X 坐标排序取源/目标，不按点击顺序；操作前把源对象放在左边、目标放右边，避免套反。', gif: '/eprinttools-files/front-end/features/objreplacer/objreplacer-left-source.gif' }
    ],
    audiences: [
      { title: '包装结构 / 版型', body: '刀模换了，图形要套进新轮廓。', icon: '型' },
      { title: '标签 / 吊牌', body: '不同尺寸的版式复用同一套视觉。', icon: '签' },
      { title: '印前完稿', body: '客户给了新刀模，需要快速把旧稿套进去。', icon: '稿' }
    ],
    boundaries: [
      '需要两个可替换对象；不是“选一个自动找所有”。',
      '当前版本按 X 坐标决定源/目标，不按点击顺序；操作前把源对象放左侧。',
      '面板“选择源/目标”按钮当前不参与替换逻辑，页面按“选中两个对象”描述。',
      '等比缩放取较小比例，可能留白；需要铺满请用非等比。'
    ],
    faqs: [
      { q: '源对象和目标对象怎么区分？', a: '当前版本按 X 坐标排序，左侧为源、右侧为目标；操作前把源对象放在左边。' },
      { q: '为什么“选择源/目标”按钮没有生效？', a: '当前版本替换逻辑读取当前选中的两个对象，按钮只改状态文字；这是已知的交互不一致，后续版本会统一。' },
      { q: '会拉伸图形吗？', a: '勾选“等比”时取宽/高比例中较小的一个，不会拉伸；需要铺满可取消等比。' },
      { q: '保留原对象有什么用？', a: '保留源对象，把目标对象套上去，适合做对比或保留旧版。' },
      { q: '支持哪些对象？', a: '路径/复合路径；组内部分选中会做逻辑合并。位图、文字等请先转换。' }
    ]
  },

  barcodegena: {
    key: 'barcodegena',
    en: 'BARCODEGENA',
    name: '小帮条形码生成器',
    tagline: '在 Illustrator 快速生成、精准识别、校正常见类型条形码',
    summary: '常用一维码、变量标记、HRI 文本、条宽缩减和三种识别方式。生成的是矢量条码，缩放不糊，直接进印前流程。',
    accent: '#0F766E',
    cover: 'barcode',
    heroGif: '/eprinttools-files/front-end/features/barcodegena/barcode-generate.gif',
    chips: ['矢量条码', '条宽缩减', '识别回填'],
    ai: 'Illustrator 2019+',
    platform: 'Windows 10/11',
    format: 'ZIP',
    trialDays: 30,
    versionNote: '当前版本不生成二维码，专注一维码/条形码。',
    pains: [
      { title: '网页生成器只给图片', body: '条码需要矢量、精确尺寸和条宽；图片条码放大就糊，印刷容易出问题。' },
      { title: '条宽缩减靠猜', body: '不同印刷条件要调条宽，靠肉眼或反复打样，效率低。' },
      { title: '来稿条码不知道内容', body: '供应商文件里的条码需要识别、核对，再重新生成可印刷的矢量条码。' }
    ],
    steps: [
      { title: '选类型、输数据', body: '选码制，输入内容，确认校验位。' },
      { title: '标记或直接生成', body: '有版式就标记条码/数据变量；没有就直接生成。' },
      { title: '调条宽与颜色', body: '按印刷条件设置 BWR，设置条码颜色。' },
      { title: '识别核对', body: '对来稿做选中/截屏/矩形识别，必要时一键回填。' }
    ],
    features: [
      { title: '矢量条码生成', body: '基于 Zint 生成真正矢量条码组，同时带 HRI 可读文本；放大缩小不糊，可直接进入印前流程。', large: true, gif: '/eprinttools-files/front-end/features/barcodegena/barcode-generate.gif' },
      { title: '变量标记', body: '用“设置条码变量 / 设置数据变量”把生成位置绑定到稿子；后续换数据只更新内容，不用重新摆位。', large: true, gif: '/eprinttools-files/front-end/features/barcodegena/barcode-variable.gif' },
      { title: '条宽缩减 BWR', body: '按 mm 调整已标记条码组的条宽，适配印刷增益；只对由本插件生成且已标记的条码组生效。', gif: '/eprinttools-files/front-end/features/barcodegena/barcode-bwr.gif' },
      { title: '三种识别方式', body: '支持选中识别、截屏识别、矩形识别，读取供应商来稿里的条码类型和内容。', gif: '/eprinttools-files/front-end/features/barcodegena/barcode-recognize.gif' },
      { title: '识别后生成', body: '识别成功后可直接回填并重新生成矢量条码，避免把图片条码直接拿去印刷。', gif: '/eprinttools-files/front-end/features/barcodegena/barcode-recognize-generate.gif' },
      { title: '校验位与码制', body: 'EAN13 / UPCA / EAN8 / ITF14 自动显示校验位；覆盖常用一维码，不生成二维码。', gif: '/eprinttools-files/front-end/features/barcodegena/barcode-checksum.gif' }
    ],
    audiences: [
      { title: '包装印前', body: '条码位置、尺寸、条宽需要精确控制。', icon: '码' },
      { title: '标签 / 吊牌', body: '多 SKU、多变量数据，反复生成。', icon: '签' },
      { title: 'QA / 完稿检查', body: '识别供应商来稿条码，核对类型和内容。', icon: '检' }
    ],
    boundaries: [
      '不生成二维码；代码明确排除 QR。页面只写条形码/一维码。',
      '不是 GS1 合规审核器；GS1-128 只提供格式提示和输入支持。',
      '条宽缩减只对“由本插件生成且已标记”的条码组生效。',
      '识别受图片分辨率、模块宽度、对比度影响；失败时换更清晰的图。'
    ],
    faqs: [
      { q: '支持二维码吗？', a: '当前版本不生成二维码，专注一维码/条形码。' },
      { q: '条宽缩减怎么用？', a: '先用“设置条码变量”标记由本插件生成的条码组，选中它，输入 mm 值，点“更新条宽”。' },
      { q: '识别失败怎么办？', a: '换更清晰的图、放大截屏区域、保证条码完整；插件会提示分辨率/模块宽度/对比度等原因。' },
      { q: '生成的是图片还是矢量？', a: '矢量条码组 + 文本，缩放不糊。' },
      { q: '能直接用于 GS1 吗？', a: '提供 GS1-128 格式提示和校验位支持，但最终合规要按 GS1 标准核对。' }
    ]
  },

  colormanage: {
    key: 'colormanage',
    en: 'COLORMANAGE',
    name: '色彩管理',
    tagline: '把文档里的颜色摊开，一次替换、定位、清理。',
    summary: '列出每种颜色的使用数量、色块和显隐状态；支持 CMYK 快捷替换、颜色混合器、标记替换、定位和删除。印前清色，不用再逐层翻。',
    accent: '#C2410C',
    cover: 'color',
    heroGif: '/eprinttools-files/front-end/features/colormanage/colormanage-cmyk-replace.gif',
    chips: ['颜色盘点', 'CMYK 替换', '定位清理'],
    ai: 'Illustrator 2019+',
    platform: 'Windows 10/11',
    format: 'ZIP',
    trialDays: 30,
    versionNote: '当前版本“套版色”按钮在代码里是隐藏状态，页面不把它作为卖点。',
    pains: [
      { title: '文件里几十种颜色', body: '专色、CMYK、灰度混在一起，不知道哪些是多余的，靠图层面板逐层找。' },
      { title: '品牌色不知道谁在用', body: '要统一替换，却不知道哪些对象用了它；组内和隐藏对象更容易漏。' },
      { title: '客户来稿一堆杂色', body: '印前要清理，否则影响制版和印刷；逐个图层找效率太低。' }
    ],
    steps: [
      { title: '读取', body: '选“仅选中内容”或整篇，点“读取颜色”。' },
      { title: '筛选', body: '看数量、色块、眼睛状态，勾选要处理的颜色。' },
      { title: '替换/定位', body: 'C/M/Y/K 快捷替换，或混合器调色；需要找对象就“定位”。' },
      { title: '清理', body: '删除使用某颜色的对象，或标记后统一替换。' }
    ],
    features: [
      { title: '颜色盘点', body: '按整篇或仅选中内容读取颜色，合并相同值，统计使用对象数量，并显示色块与显隐状态。', large: true, gif: '/eprinttools-files/front-end/features/colormanage/colormanage-read.gif' },
      { title: 'CMYK 快捷替换', body: '选中颜色后直接修改 C/M/Y/K 数值，或用颜色混合器调色，再替换到整篇文档或当前选区。', large: true, gif: '/eprinttools-files/front-end/features/colormanage/colormanage-cmyk-replace.gif' },
      { title: '标记替换', body: '先标记一个目标颜色，再选择新颜色执行替换；适合统一品牌色、清理客户来稿杂色。', gif: '/eprinttools-files/front-end/features/colormanage/colormanage-mark-replace.gif' },
      { title: '定位', body: '选中使用某颜色的对象并定位到画布，不会自动缩放视图；方便继续检查或修改。', gif: '/eprinttools-files/front-end/features/colormanage/colormanage-locate.gif' },
      { title: '删除使用颜色的对象', body: '删除的是使用选中颜色的对象，不是删除色板；执行前会确认，操作前建议先保存副本。', gif: '/eprinttools-files/front-end/features/colormanage/colormanage-delete.gif' },
      { title: '专色 / 灰度兼容', body: '支持读取专色、灰度，并在替换时尝试转 CMYK；非 CMYK 文档会提示，转换后外观可能变化。', gif: '/eprinttools-files/front-end/features/colormanage/colormanage-spot-replace.gif' }
    ],
    audiences: [
      { title: '印前 / 制版', body: '清理杂色、专色，统一品牌色。', icon: '印' },
      { title: '包装设计', body: '控制油墨数量，检查隐藏对象里的颜色。', icon: '包' },
      { title: '品牌 / 完稿', body: '客户来稿颜色不一致，快速盘点和替换。', icon: '牌' }
    ],
    boundaries: [
      '以 CMYK 工作流为主；非 CMYK 文档会提示，不能保证所有颜色模型等价转换。',
      '“删除”删除的是使用选中颜色的对象，不是删除色板；操作前保存副本。',
      '“套版色”按钮当前隐藏/停用，不要作为卖点。',
      '专色转 CMYK 可能改变外观，替换前先对比/打样。'
    ],
    faqs: [
      { q: '会统计隐藏对象吗？', a: '会；列表里的小眼睛表示该颜色的对象是否全部隐藏。可以用它找出藏在隐藏层里的颜色。' },
      { q: '“删除”是删颜色还是删对象？', a: '删除使用选中颜色的对象。危险操作，先保存副本。' },
      { q: '支持专色吗？', a: '支持读取专色，并在替换时尝试转 CMYK；转换后外观可能变化。' },
      { q: '为什么提示不是 CMYK 文档？', a: '插件的替换逻辑以 CMYK 为主；非 CMYK 文档请先转换或谨慎操作。' },
      { q: '能定位到对象吗？', a: '“定位”会选中使用该颜色的对象，方便你继续处理。' }
    ]
  }
}

export const AIP_PLUGIN_KEYS = Object.keys(AIP_PLUGIN_CONTENT)

export function getAipPluginContent(pluginKey) {
  return AIP_PLUGIN_CONTENT[pluginKey] || null
}
